import type { Editor } from '@tiptap/react';
import { getAiToolkit } from '@tiptap-pro/ai-toolkit';
import type { UIMessage } from 'ai';

import type { AgentContext } from '@/hooks/llm/prompt-invocation-builders';

const isSyntheticPrefetchToolPart = (part: { type?: string; toolCallId?: string }) =>
  typeof part.type === 'string' &&
  part.type.startsWith('tool-') &&
  typeof part.toolCallId === 'string' &&
  part.toolCallId.startsWith('prefetch_');

export function prefetchDocumentContent(editor: Editor, toolName: 'tiptapRead' | 'tiptapReadSelection'): unknown {
  const toolkit = getAiToolkit(editor);
  const toolCallId = `prefetch_${crypto.randomUUID()}`;

  const result = toolkit.streamTool({
    toolCallId,
    toolName,
    input: toolName === 'tiptapRead' ? { from: 0 } : {},
    hasFinished: true,
    chunkSize: Number.MAX_SAFE_INTEGER,
  });

  return result.output;
}

export function buildPrefetchMessages(
  text: string,
  toolName: 'tiptapRead' | 'tiptapReadSelection',
  toolOutput: unknown,
  context?: AgentContext
): UIMessage[] {
  const prefetchedParts: UIMessage['parts'] = [
    {
      type: `tool-${toolName}` as const,
      toolCallId: `prefetch_doc_${crypto.randomUUID()}`,
      state: 'output-available',
      input: toolName === 'tiptapRead' ? { from: 0 } : {},
      output: toolOutput,
    },
  ];

  if (context) {
    prefetchedParts.push({
      type: 'tool-getDocumentContext' as const,
      toolCallId: `prefetch_context_${crypto.randomUUID()}`,
      state: 'output-available',
      input: {},
      output: context,
    } as any);
  }

  return [
    {
      id: crypto.randomUUID(),
      role: 'user',
      parts: [{ type: 'text', text }],
    },
    {
      id: crypto.randomUUID(),
      role: 'assistant',
      parts: prefetchedParts,
    },
  ];
}

export function stripSyntheticPrefetchAssistantMessages(messages: UIMessage[]): UIMessage[] {
  let didChange = false;

  const sanitized = messages.flatMap((message) => {
    if (message.role !== 'assistant') {
      return [message];
    }

    const parts = message.parts.filter((part) => !isSyntheticPrefetchToolPart(part as { type?: string; toolCallId?: string }));
    if (parts.length === message.parts.length) {
      return [message];
    }

    didChange = true;
    return parts.length > 0 ? [{ ...message, parts }] : [];
  });

  return didChange ? sanitized : messages;
}
